// ─────────────────────────────────────────────────────────────
// MOTEUR DE SYNCHRONISATION DES REVENUS — Zifek Finance
// Regroupe dans une même logique les revenus de tous les modules :
// produits, produits digitaux, services, formations, rendez-vous
// (zcalendar) et réservations d'hébergement.
//
// Chaque source est catégorisée finement à partir du type de commande,
// puis rattachée à la trésorerie sous forme d'une écriture de revenu
// (idempotente grâce à `source_ref` : jamais de doublon).
// ─────────────────────────────────────────────────────────────

export interface RevenueSourceMeta {
  key: string;
  label: string;
  icon: string;
  color: string;
}

/** Libellés & styles des sources de revenus (partagés UI + synchro). */
export const REVENUE_SOURCE_META: RevenueSourceMeta[] = [
  { key: 'product', label: 'Produits', icon: 'ri-shopping-bag-3-line', color: 'bg-primary-50 text-primary-700' },
  { key: 'digital', label: 'Produits digitaux', icon: 'ri-download-cloud-2-line', color: 'bg-accent-50 text-accent-700' },
  { key: 'service', label: 'Services', icon: 'ri-service-line', color: 'bg-secondary-50 text-secondary-700' },
  { key: 'formation', label: 'Formations', icon: 'ri-graduation-cap-line', color: 'bg-accent-50 text-accent-700' },
  { key: 'appointment', label: 'Rendez-vous', icon: 'ri-calendar-schedule-line', color: 'bg-secondary-50 text-secondary-700' },
  { key: 'hebergement', label: 'Hébergement', icon: 'ri-hotel-bed-line', color: 'bg-primary-50 text-primary-700' },
];

const OTHER_META: RevenueSourceMeta = {
  key: 'other',
  label: 'Autres revenus',
  icon: 'ri-money-dollar-circle-line',
  color: 'bg-background-100 text-foreground-600',
};

/** Déduit la catégorie de revenu d'une commande à partir de son type. */
export function revenueCategoryFromOrderType(type: string | null | undefined): RevenueSourceMeta {
  const t = (type || '').trim().toLowerCase();
  if (t.includes('digital')) return REVENUE_SOURCE_META.find((m) => m.key === 'digital') as RevenueSourceMeta;
  if (t.includes('formation') || t.includes('cours')) return REVENUE_SOURCE_META.find((m) => m.key === 'formation') as RevenueSourceMeta;
  if (t.includes('service')) return REVENUE_SOURCE_META.find((m) => m.key === 'service') as RevenueSourceMeta;
  if (t.includes('rdv') || t.includes('rendez') || t.includes('reserv') || t.includes('booking') || t.includes('appointment')) {
    return REVENUE_SOURCE_META.find((m) => m.key === 'appointment') as RevenueSourceMeta;
  }
  if (t.includes('hebergement') || t.includes('hébergement')) return REVENUE_SOURCE_META.find((m) => m.key === 'hebergement') as RevenueSourceMeta;
  return REVENUE_SOURCE_META.find((m) => m.key === 'product') as RevenueSourceMeta;
}

/** Retrouve les métadonnées d'une source à partir du libellé stocké sur l'écriture. */
export function revenueMetaForLabel(label: string | null | undefined): RevenueSourceMeta {
  if (!label) return OTHER_META;
  return REVENUE_SOURCE_META.find((m) => m.label === label) || { ...OTHER_META, label };
}